import {useLandAccountInfos} from '#/hooks/query/use-land-account-info';
import {useLandMarketInfos} from '#/hooks/query/use-land-market-info';
import {usePortfolioLandsAndAssets} from '#/hooks/query/use-portfolio-lands';
import {
  LandAccountInfo,
  LandMarketInfo,
  TransactionPortfolioValue,
  getLandPortfolioValue,
} from '@shareland/shareland-common';
import {Hex} from 'viem';

export interface Portfolio {
  totalAssetsValue: number;
}

type LandGroup = {
  portfolioValue: TransactionPortfolioValue;
  landMarketInfo: LandMarketInfo;
  landAccountInfo: LandAccountInfo;
};

export const usePortfolio = (account: Hex) => {
  const {data: portfolioLands = [], isLoading: portfolioLandsIsLoading} = usePortfolioLandsAndAssets(account);
  const {data: landMarketInfos = [], isLoading: landMarketInfosIsLoading} = useLandMarketInfos(
    portfolioLands.map(({land}) => land),
  );
  const {data: landAccountInfos = [], isLoading: landAccountInfosIsLoading} = useLandAccountInfos(
    portfolioLands.map(({land}) => land),
    account,
  );

  const totalAssetsValue = portfolioLands
    .map(({portfolioValue}, index) => ({
      portfolioValue,
      landMarketInfo: landMarketInfos[index],
      landAccountInfo: landAccountInfos[index],
    }))
    .filter((entry): entry is LandGroup => !!entry.landMarketInfo && !!entry.landAccountInfo)
    .map(({portfolioValue, landMarketInfo, landAccountInfo}) =>
      getLandPortfolioValue(
        {
          availableSupply: BigInt(landMarketInfo.availableSupply),
          liquidity: BigInt(landMarketInfo.totalLiquidity),
          lpTokenSupply: BigInt(landMarketInfo.lpTokenSupply),
          oraclePrice: BigInt(landMarketInfo.oraclePrice),
          marketCollateralPositionLandTokenAmount: BigInt(landMarketInfo.marketCollateralPositionLandTokenAmount),
          marketFormulaWeightStablePlusLandToken: BigInt(
            landMarketInfo.marketFormulaWeigths.initialStablePlusLandToken,
          ),
          marketFormulaWeightOraclePrice: BigInt(landMarketInfo.marketFormulaWeigths.initialOraclePrice),
        },
        {
          liquidityProviderTokenBalance: landAccountInfo.lpTokenAmount,
          landTokenAmount: landAccountInfo.landTokenAmount,
          landTokenAmountInCollateral: landAccountInfo.borrowedLandTokenAmount,
          stableTokenAmountInCollateral: landAccountInfo.stableTokenInCollateralAmount,
          orders: portfolioValue.orders,
        },
      ),
    )
    .reduce((acc, curr) => acc + curr, 0);

  return {
    data: {
      totalAssetsValue,
    },
    isLoading: portfolioLandsIsLoading || landMarketInfosIsLoading || landAccountInfosIsLoading,
  };
};
